import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NewsItem, newsService } from "~/services/firebaseService";
import AppLayout, { SortOrder } from "~/pages/AppLayout/AppLayout";
import { Card } from "~/components/Card";

export default function News() {
  const { t } = useTranslation();
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest");

  useEffect(() => {
    async function fetchData() {
      try {
        const fetchedData = await newsService.getAllNewsItems();
        setNews(fetchedData);
      } catch (err) {
        setError("Failed to fetch news");
        console.error("Error fetching news:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  // Sort news by publication date based on sortOrder
  const sortedNews = useMemo(() => {
    return [...news].sort((a, b) => {
      const dateA = a.publishedAt?.toDate().getTime() ?? 0;
      const dateB = b.publishedAt?.toDate().getTime() ?? 0;
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });
  }, [news, sortOrder]);

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    const fetchData = async () => {
      try {
        const fetchedData = await newsService.getAllNewsItems();
        setNews(fetchedData);
      } catch (err) {
        setError("Failed to fetch news");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  };

  return (
    <AppLayout
      title={t("news.title")}
      loading={loading}
      error={error}
      emptyTitle="No News Available"
      emptyMessage="We're working on adding new news items. Please check back soon!"
      onRetry={handleRetry}
      sortOrder={sortOrder}
      onSortChange={setSortOrder}
    >
      {sortedNews.map((newsItem) => (
        <Card
          key={newsItem.id}
          id={newsItem.id!}
          title={newsItem.title}
          description={newsItem.excerpt}
          image={newsItem.image}
          href={`/news/${newsItem.id}`}
          category={newsItem.category}
          meta={{
            author: `By ${newsItem.author}`,
            date: newsItem.publishedAt?.toDate().toLocaleDateString(),
          }}
        />
      ))}
    </AppLayout>
  );
}
